import SelectorItem from '../../components/SelectorItem';
import InputItem from '../../components/InputItem';
import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Drawer, message, Modal, InputNumber } from 'antd';
import { PlusOutlined, DeleteOutlined, CheckOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';

import ItemTabs from './ItemTabs';
import PlayerList from './PlayerList';
import NoSchedule from './NoSchedule';
import EditSchedules from './EditSchedules';
import RefereeSelector from '../../components/RefereeSelector';
import ExportButton from '../../components/ExportButton';

import request from '@/request';

const getStatusInfo = (s) => {
  const status = s || '未开始';

  const statusMap = {
    未开始: {
      label: '未开始',
      classes: 'text-slate-600 bg-slate-100',
      icon: (
        <svg
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          className="w-4 h-4"
        >
          <circle cx="12" cy="12" r="9" />
          <path d="M12 7v6l4 2" />
        </svg>
      ),
    },
    进行中: {
      label: '进行中',
      classes: 'text-green-700 bg-green-100',
      icon: (
        <svg
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          className="w-4 h-4"
        >
          <path d="M5 3v18l15-9L5 3z" />
        </svg>
      ),
    },
    已结束: {
      label: '已结束',
      classes: 'text-blue-700 bg-blue-100',
      icon: (
        <svg
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          className="w-4 h-4"
        >
          <path d="M20 6L9 17l-5-5" />
        </svg>
      ),
    },
    已取消: {
      label: '已取消',
      classes: 'text-slate-600 bg-slate-100',
      icon: (
        <svg
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          className="w-4 h-4"
        >
          <path
            d="M18 6L6 18M6 6l12 12"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      ),
    },
  };

  return statusMap[status] || statusMap['未开始'];
};

const SItem = ({
  s,
  idx,
  unit,
  showPlayersDrawer,
  editable,
  onChange,
  showCoachDrawer,
  setEditData,
}) => {
  const mainReferee =
    (s?.refereeList || [])
      .filter((r) => r.role === 1)
      .map((r) => r.name)
      .join('、') || '未分配';
  const viceReferee =
    (s?.refereeList || [])
      .filter((r) => r.role === 0)
      .map((r) => r.name)
      .join('、') || '未分配';

  const handleChange = (field, value) => {
    if (onChange) onChange(idx, field, value);
  };

  const handleDelete = (idx) => {
    if (setEditData) {
      setEditData((prev) => prev.filter((_, i) => i !== idx));
    }
  };
  return (
    <div
      className={`w-100 ${editable ? 'h-75' : 'h-60'} rounded-xl shadow-sm border border-slate-100 p-5 flex flex-col min-w-[360px] transition-shadow hover:shadow-md group items-start justify-around text-sm text-slate-600`}
    >
      <div className="flex items-center justify-between mb-1 w-full">
        <div className="text-base font-semibold text-slate-900 truncate">
          场次{idx + 1}
        </div>
        <div className="ml-4 flex-shrink-0 flex flex-col items-end">
          {editable && (
            <button
              type="button"
              title="删除"
              className="inline-flex items-center gap-1 px-2 py-1 rounded-md bg-red-50 text-red-700 hover:bg-red-100"
              onClick={() => handleDelete(idx)}
            >
              <DeleteOutlined className="text-lg" />
              <span className="text-sm">删除</span>
            </button>
          )}
          {!editable &&
            (() => {
              const info = getStatusInfo(s.statueText);
              const isActive = s.statueText === '进行中';
              return (
                <span
                  className={`inline-flex items-center gap-2 text-sm font-medium px-3 py-1.5 rounded-full ${info.classes}`}
                >
                  <span className={isActive ? 'animate-pulse' : ''}>
                    {info.icon}
                  </span>
                  {info.label}
                </span>
              );
            })()}
        </div>
      </div>
      {/* 场地 */}
      <div className="w-full flex items-center">
        <span className="w-16 text-right">场地名称：</span>
        {editable ? (
          <InputItem
            value={s?.matchPlace}
            onChange={(val) => handleChange('matchPlace', val)}
            placeholder="场地"
            className="w-74"
          />
        ) : (
          <>{s?.matchPlace || '未设置'}</>
        )}
      </div>
      {/* 比赛时间 */}
      <div className="flex items-center">
        <span className="w-16 text-right">比赛时间：</span>
        {editable ? (
          <SelectorItem
            type="time"
            value={
              s?.matchStartTime
                ? [dayjs(s?.matchStartTime), dayjs(s?.matchEndTime)]
                : []
            }
            onChange={(times) => {
              const start = times && times[0] ? times[0] : null;
              const end = times && times[1] ? times[1] : null;
              handleChange('matchStartTime', start ? start.toISOString() : '');
              handleChange('matchEndTime', end ? end.toISOString() : '');
            }}
            className="w-74"
          />
        ) : (
          <>
            {dayjs(s?.matchStartTime).format('YYYY-MM-DD HH:mm')} -{' '}
            {dayjs(s?.matchEndTime).format('YYYY-MM-DD HH:mm')}
          </>
        )}
      </div>
      {/* 人数+按钮 */}
      <div className="w-full flex items-center justify-between">
        <div className="flex items-center mr-2">
          <span className="w-16 text-right">{unit}数：</span>
          {editable ? (
            <InputItem
              type="number"
              min={0}
              suffix={unit}
              value={s?.player}
              onChange={(val) => handleChange('player', Number(val))}
              placeholder={unit + '数'}
              className="w-74"
            />
          ) : (
            <>
              {s?.player}
              {unit}
            </>
          )}
        </div>
        {!editable && (s?.statueText === '进行中' || s?.statueText === '已结束') && (
          <button
            className="px-3 py-1.5 bg-blue-50 text-blue-700 text-xs font-medium rounded hover:bg-blue-100 transition-colors cursor-pointer"
            onClick={() => showPlayersDrawer(s?.scheduleId)}
          >
            查看选手
          </button>
        )}
      </div>
      <div className="w-full flex items-center">
        <span className="w-16 text-right">主裁判：</span>
        <span>{mainReferee}</span>
        {editable && (
          <button
            type="button"
            className="flex items-center gap-1 text-sm text-blue-600 hover:bg-blue-50 hover:text-blue-700 px-2 py-0.5 rounded cursor-pointer transition"
            onClick={() => showCoachDrawer(idx, 1)}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth={2}
              className="w-4 h-4"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4z"
              />
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M6 20v-1a4 4 0 014-4h4"
              />
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M19 11v2m1-1h-2"
              />
            </svg>
            <span>去指定</span>
          </button>
        )}
      </div>
      <div className="flex items-center justify-between">
        <span className="w-16 text-right">助理裁判：</span>
        <span>{viceReferee}</span>
        {editable && (
          <button
            type="button"
            className="flex items-center gap-1 text-sm text-blue-600 hover:bg-blue-50 hover:text-blue-700 px-2 py-0.5 rounded cursor-pointer transition"
            onClick={() => showCoachDrawer(idx, 0)}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth={2}
              className="w-4 h-4"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4z"
              />
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M6 20v-1a4 4 0 014-4h4"
              />
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M19 11v2m1-1h-2"
              />
            </svg>
            <span>去指定</span>
          </button>
        )}
      </div>
    </div>
  );
};

/* === P1 评奖方式相关常量 === */
const PRIZE_NAMES = [
  '一等奖', '二等奖', '三等奖', '四等奖', '五等奖',
  '六等奖', '七等奖', '八等奖', '九等奖', '十等奖',
];

const DEFAULT_PRIZES = [
  { label: '一等奖', value: 15 },
  { label: '二等奖', value: 25 },
  { label: '三等奖', value: 50 },
];

const DEFAULT_AWARD_CONFIG = {
  mode: 'traditional',
  rounding: 'ceil',
  prizes: [...DEFAULT_PRIZES],
};

/* === P2 比赛轮次相关常量 === */
const ELIMINATION_OPTIONS = ['排位法', '单败淘汰', '双败淘汰'];
const MATCH_TYPE_OPTIONS = [
  '排位赛', 'BO赛', '循环赛',
  /* '回合攻防赛', '团队接力赛', '擂台赛', '单循环赛', '双循环赛', */
];

/**
 * AwardSection：P1 评奖方式配置区块
 * props: { awardConfig: { mode, rounding, prizes }, onChange: function, readOnly: boolean, applyCount: number|null, unit: string }
 */
const AwardSection = ({ awardConfig, onChange, readOnly = false, applyCount = null, unit = '人' }) => {
  const { mode, rounding, prizes } = awardConfig;
  const total = prizes.reduce((sum, p) => sum + (Number(p.value) || 0), 0);
  const isOver = total > 100;

  const handleModeChange = (newMode) => {
    onChange({
      ...awardConfig,
      mode: newMode,
      prizes: newMode === 'percentage' ? [...DEFAULT_PRIZES] : prizes,
    });
  };

  const handlePrizeValueChange = (idx, val) => {
    const next = prizes.map((p, i) => (i === idx ? { ...p, value: val ?? 0 } : p));
    onChange({ ...awardConfig, prizes: next });
  };

  const handleDeletePrize = (idx) => {
    if (prizes.length <= 1) return;
    const next = prizes
      .filter((_, i) => i !== idx)
      .map((p, i) => ({ ...p, label: PRIZE_NAMES[i] || `${i + 1}等奖` }));
    onChange({ ...awardConfig, prizes: next });
  };

  const handleAddPrize = () => {
    const next = [
      ...prizes,
      { label: PRIZE_NAMES[prizes.length] || `${prizes.length + 1}等奖`, value: 0 },
    ];
    onChange({ ...awardConfig, prizes: next });
  };

  return (
    <div className="bg-white rounded-xl shadow-sm p-6 space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-base font-semibold text-slate-800">选择评奖方式</h3>
        {applyCount !== null && (
          <span className="text-sm text-slate-500">报名数量：<span className="text-slate-800 font-medium">{applyCount}</span>人(队)</span>
        )}
      </div>
      <div className="flex flex-col border border-slate-200 rounded-xl overflow-hidden">
        {/* 传统模式行 */}
        <div
          className={`flex items-center gap-3 p-4 transition-all ${readOnly ? 'cursor-default' : 'cursor-pointer'} ${
            mode === 'traditional' ? 'bg-blue-50' : 'bg-white hover:bg-slate-50'
          }`}
          onClick={() => !readOnly && handleModeChange('traditional')}
        >
          <div
            className={`w-4 h-4 rounded-full border-2 flex-shrink-0 flex items-center justify-center ${
              mode === 'traditional' ? 'border-blue-500' : 'border-slate-300'
            }`}
          >
            {mode === 'traditional' && <div className="w-2 h-2 rounded-full bg-blue-500" />}
          </div>
          <span className="font-medium text-slate-800 w-20 flex-shrink-0">传统模式</span>
          <div className="flex gap-2">
            {['冠军', '亚军', '季军'].map((t) => (
              <span key={t} className="px-3 py-1 bg-slate-100 text-slate-600 text-sm rounded-full">
                {t}
              </span>
            ))}
          </div>
        </div>

        {/* 百分比模式行 */}
        <div className="border-t border-slate-200">
          <div
            className={`flex items-center justify-between p-4 transition-all ${readOnly ? 'cursor-default' : 'cursor-pointer'} ${
              mode === 'percentage' ? 'bg-blue-50' : 'bg-white hover:bg-slate-50'
            }`}
            onClick={() => !readOnly && handleModeChange('percentage')}
          >
            <div className="flex items-center gap-3">
              <div
                className={`w-4 h-4 rounded-full border-2 flex-shrink-0 flex items-center justify-center ${
                  mode === 'percentage' ? 'border-blue-500' : 'border-slate-300'
                }`}
              >
                {mode === 'percentage' && <div className="w-2 h-2 rounded-full bg-blue-500" />}
              </div>
              <span className="font-medium text-slate-800 w-20 flex-shrink-0">百分比模式</span>
              {mode !== 'percentage' && (
                <span className="text-sm text-slate-500">按奖项占比分配，支持自定义奖项数量与比例</span>
              )}
            </div>
            {/* 取整方式（百分比模式选中时显示在右侧） */}
            {mode === 'percentage' && (
              <div className="flex items-center gap-4" onClick={(e) => e.stopPropagation()}>
                {[
                  { val: 'ceil', label: '向上取整' },
                  { val: 'floor', label: '向下取整' },
                ].map(({ val, label }) => (
                  <label
                    key={val}
                    className="flex items-center gap-1.5 cursor-pointer"
                    onClick={() => !readOnly && onChange({ ...awardConfig, rounding: val })}
                  >
                    <div
                      className={`w-4 h-4 rounded-full border-2 flex items-center justify-center ${
                        rounding === val ? 'border-blue-500' : 'border-slate-300'
                      }`}
                    >
                      {rounding === val && <div className="w-2 h-2 rounded-full bg-blue-500" />}
                    </div>
                    <span className="text-sm text-slate-700">{label}</span>
                  </label>
                ))}
              </div>
            )}
          </div>
          {/* 奖项列表展开区 */}
          {mode === 'percentage' && (
            <div className="px-4 pb-4 pt-3 space-y-3" onClick={(e) => e.stopPropagation()}>
              {isOver && (
                <div className="text-red-500 text-xs">奖项总和超出100%，请调整</div>
              )}
              <div className="flex flex-wrap gap-x-4 gap-y-2">
                {prizes.map((p, idx) => (
                  <div key={idx} className="flex items-center gap-1.5">
                    <span className="text-sm text-slate-600 shrink-0">{p.label}</span>
                    <InputNumber
                      min={0}
                      max={100}
                      value={p.value}
                      onChange={(val) => handlePrizeValueChange(idx, val)}
                      size="small"
                      disabled={readOnly}
                      style={{ width: 150 }}
                      className={isOver ? 'border-red-400' : ''}
                      status={isOver ? 'error' : undefined}
                    />
                    <span className="text-sm text-slate-500">%</span>
                    {!readOnly && (
                      <button
                        type="button"
                        className="text-slate-400 hover:text-red-500 transition-colors disabled:opacity-40"
                        onClick={() => handleDeletePrize(idx)}
                        disabled={prizes.length <= 1}
                      >
                        <DeleteOutlined />
                      </button>
                    )}
                  </div>
                ))}
              </div>
              <div className="text-xs text-slate-500">
                合计：
                <span className={isOver ? 'text-red-500 font-medium' : 'text-slate-700'}>
                  {total}%
                </span>
              </div>
              {!readOnly && (
              <button
                type="button"
                className="flex items-center gap-1 text-sm text-blue-600 hover:text-blue-700"
                onClick={handleAddPrize}
              >
                <PlusOutlined />
                <span>添加奖项</span>
              </button>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

/**
 * RoundModal：P2 比赛轮次新增/编辑弹窗
 * props:
 *   open         - 是否显示弹窗
 *   onClose      - 关闭回调
 *   onConfirm    - 确认回调，参数为表单数据
 *   initialData  - 编辑时的初始数据，新增时为 null
 *   maxApply     - 本轮最大可晋级人数限制
 *   contestFormat - 竞赛赛制：1=竞速赛（排位法/排位赛），2=对抗赛（淘汰/BO赛）
 */
const RoundModal = ({ open, onClose, onConfirm, initialData, maxApply = null, contestFormat }) => {
  const [formData, setFormData] = useState({
    eliminationType: '',
    advanceCount: 0,
    matchType: '',
    boCount: 1,
    roundCount: 1,
    allowByeMatch: 0,
  });
  const [errors, setErrors] = useState({});

  /*
   * 根据 contestFormat 动态计算本次弹窗可用的海选方法与比赛方式选项：
   * - contestFormat === 1（竞速赛）：海选方法仅 排位法，比赛方式仅 排位赛
   * - contestFormat === 2（对抗赛）：海选方法为 单败淘汰/双败淘汰，比赛方式仅 BO赛
   * - 未设置时保留全部选项作为兜底，避免页面异常
   */
  const availableEliminationOptions = useMemo(() => {
    if (contestFormat === 1) return ['排位法'];
    if (contestFormat === 2) return ['单败淘汰', '双败淘汰'];
    return ELIMINATION_OPTIONS;
  }, [contestFormat]);

  /*
   * 对抗赛（contestFormat === 2）新增「循环赛」选项；其他赛制不变
   */
  const availableMatchTypeOptions = useMemo(() => {
    if (contestFormat === 1) return ['排位赛'];
    if (contestFormat === 2) return ['BO赛', '循环赛'];
    return MATCH_TYPE_OPTIONS;
  }, [contestFormat]);

  useEffect(() => {
    if (open) {
      const baseData = initialData
        ? {
            eliminationType: initialData.eliminationType || '',
            advanceCount: initialData.advanceCount ?? 0,
            matchType: initialData.matchType || '',
            boCount: initialData.boCount ?? 1,
            roundCount: initialData.roundCount ?? 1,
            allowByeMatch: initialData.allowByeMatch ?? 0,
          }
        : { eliminationType: '', advanceCount: 0, matchType: '', boCount: 1, roundCount: 1, allowByeMatch: 0 };

      /*
       * 自动预填唯一选项：当可用选项只有一个时无需用户手动点选，
       * 且若当前值不在可用选项内则重置为空，防止赛制切换后残留无效值
       */
      if (availableEliminationOptions.length === 1) {
        baseData.eliminationType = availableEliminationOptions[0];
      } else if (!availableEliminationOptions.includes(baseData.eliminationType)) {
        baseData.eliminationType = '';
      }
      if (availableMatchTypeOptions.length === 1) {
        baseData.matchType = availableMatchTypeOptions[0];
      } else if (!availableMatchTypeOptions.includes(baseData.matchType)) {
        baseData.matchType = '';
      }

      /*
       * BO赛约束：当比赛方式确定为 BO赛 时（含自动预填与已有数据），
       * boCount 必须为正奇数（不能为 0 或偶数）。
       * - 为 0 或负数时重置为默认值 1
       * - 为偶数时自动 +1 修正为奇数
       */
      if (baseData.matchType === 'BO赛') {
        const bc = Number(baseData.boCount) || 0;
        if (bc <= 0) {
          baseData.boCount = 1;
        } else if (bc % 2 === 0) {
          baseData.boCount = bc + 1;
        }
      }

      setFormData(baseData);
      setErrors({});
    }
  }, [open, initialData, availableEliminationOptions, availableMatchTypeOptions]);

  /*
   * 通用表单字段变更处理；当比赛方式切换为「循环赛」时，
   * 自动清空海选方法（循环赛无需填写海选方法）
   */
  const handleFieldChange = (field, value) => {
    setFormData((prev) => {
      const next = { ...prev, [field]: value };
      if (field === 'matchType' && value === '循环赛') {
        next.eliminationType = '';
      }
      return next;
    });
    setErrors((prev) => ({ ...prev, [field]: undefined }));
  };

  const handleConfirm = () => {
    const errs = {};
    /* 循环赛不需要海选方法，跳过该字段校验 */
    if (formData.matchType !== '循环赛' && !formData.eliminationType) errs.eliminationType = '请选择海选方法';
    if (!formData.matchType) errs.matchType = '请选择比赛方式';
    if (
      maxApply !== null &&
      formData.advanceCount > 0 &&
      formData.advanceCount > maxApply
    ) {
      errs.advanceCount = `晋级人数不能超过报名人数（${maxApply}人）`;
    }
    if (Object.keys(errs).length > 0) {
      setErrors(errs);
      return;
    }
    onConfirm(formData);
  };

  const isBo = formData.matchType === 'BO赛';
  const isRoundType = formData.matchType === '回合攻防赛';
  /* 循环赛：海选方法区块显示但全部置灰，不可选择 */
  const isRoundRobin = formData.matchType === '循环赛';

  return (
    <Modal
      title="编辑比赛轮次"
      open={open}
      onCancel={onClose}
      width={480}
      footer={
        <div className="flex justify-end gap-2">
          <button
            className="px-4 py-1.5 text-sm text-slate-700 bg-slate-100 rounded-md hover:bg-slate-200 transition-colors"
            onClick={onClose}
          >
            取消
          </button>
          <button
            className="px-4 py-1.5 text-sm text-white bg-blue-600 rounded-md hover:bg-blue-700 transition-colors"
            onClick={handleConfirm}
          >
            确认
          </button>
        </div>
      }
    >
      <div className="space-y-5 py-2">
        {/* 海选方法 */}
        <div>
          <div className="text-sm text-slate-700 mb-2 font-medium">
            海选方法 <span className="text-red-500">*</span>
          </div>
          <div className="flex gap-2">
            {/* 根据 contestFormat 动态渲染海选方法选项；循环赛时全部置灰不可选 */}
            {availableEliminationOptions.map((opt) => (
              <button
                key={opt}
                type="button"
                disabled={isRoundRobin}
                className={`px-4 py-1.5 text-sm rounded-md border transition-colors ${
                  isRoundRobin
                    ? 'bg-slate-100 border-slate-200 text-slate-400 cursor-not-allowed'
                    : formData.eliminationType === opt
                    ? 'bg-blue-600 text-white border-blue-600'
                    : 'bg-white border-slate-300 text-slate-700 hover:bg-blue-50 hover:border-blue-400'
                }`}
                onClick={() => !isRoundRobin && handleFieldChange('eliminationType', opt)}
              >
                {opt}
              </button>
            ))}
          </div>
          {errors.eliminationType && (
            <div className="text-red-500 text-xs mt-1">{errors.eliminationType}</div>
          )}
        </div>

        {/* 可晋级人数 */}
        <div>
          <div className="text-sm text-slate-700 mb-2 font-medium">可晋级人数</div>
          <div className="flex items-center gap-3">
            <InputNumber
              min={0}
              value={formData.advanceCount}
              onChange={(val) => handleFieldChange('advanceCount', val ?? 0)}
              className="w-32"
            />
            {formData.advanceCount === 0 && (
              <span className="text-slate-500 text-xs">注：填写 0 为比赛最终轮次</span>
            )}
          </div>
          {errors.advanceCount && (
            <div className="text-red-500 text-xs mt-1">{errors.advanceCount}</div>
          )}
        </div>

        {/* 比赛方式 */}
        <div>
          <div className="text-sm text-slate-700 mb-2 font-medium">
            比赛方式 <span className="text-red-500">*</span>
          </div>
          <div className="flex flex-wrap gap-2">
            {/* 根据 contestFormat 动态渲染比赛方式选项 */}
            {availableMatchTypeOptions.map((opt) => (
              <button
                key={opt}
                type="button"
                className={`px-4 py-1.5 text-sm rounded-md border transition-colors ${
                  formData.matchType === opt
                    ? 'bg-blue-600 text-white border-blue-600'
                    : 'bg-white border-slate-300 text-slate-700 hover:bg-blue-50 hover:border-blue-400'
                }`}
                onClick={() => handleFieldChange('matchType', opt)}
              >
                {opt}
              </button>
            ))}
          </div>
          {errors.matchType && (
            <div className="text-red-500 text-xs mt-1">{errors.matchType}</div>
          )}
        </div>

        {/* BO次数 + 轮空队伍（条件显示，仅 BO赛） */}
        {isBo && (
          <>
            <div>
              <div className="text-sm text-slate-700 mb-2 font-medium">BO次数</div>
              <div className="flex items-center gap-3">
                <InputNumber
                  min={1}
                  value={formData.boCount}
                  onChange={(val) => {
                    const v = val ?? 1;
                    /*
                     * 仅允许奇数：若新值为偶数，根据增减方向跳到最近奇数。
                     * - 新值 < 当前值（向下减）：跳到 v-1（上一个奇数），最小为 1
                     * - 新值 > 当前值（向上加）：跳到 v+1（下一个奇数）
                     */
                    if (v % 2 === 0) {
                      const next = v < formData.boCount ? Math.max(1, v - 1) : v + 1;
                      handleFieldChange('boCount', next);
                    } else {
                      handleFieldChange('boCount', Math.max(1, v));
                    }
                  }}
                  className="w-32"
                />
                <span className="text-slate-500 text-xs">注：只可填写奇数</span>
              </div>
            </div>

            {/* 轮空队伍：0=直接晋级，1=与败方对战 */}
            <div>
              <div className="text-sm text-slate-700 mb-2 font-medium">轮空队伍</div>
              <div className="flex gap-2">
                {[{ value: 0, label: '直接晋级' }, { value: 1, label: '与败方对战' }].map((opt) => (
                  <button
                    key={opt.value}
                    type="button"
                    className={`px-4 py-1.5 text-sm rounded-md border transition-colors ${
                      formData.allowByeMatch === opt.value
                        ? 'bg-blue-600 text-white border-blue-600'
                        : 'bg-white border-slate-300 text-slate-700 hover:bg-blue-50 hover:border-blue-400'
                    }`}
                    onClick={() => handleFieldChange('allowByeMatch', opt.value)}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>
          </>
        )}

        {/* 回合数（条件显示） */}
        {isRoundType && (
          <div>
            <div className="text-sm text-slate-700 mb-2 font-medium">回合数</div>
            <InputNumber
              min={1}
              value={formData.roundCount}
              onChange={(val) => handleFieldChange('roundCount', val ?? 1)}
              className="w-32"
            />
          </div>
        )}
      </div>
    </Modal>
  );
};

export default function ItemSchedules({ event, items }) {
  // const [editScheduleDrawer, setEditScheduleDrawer] = useState(false);
  const [activeItem, setActiveItem] = useState(items[0] || null);
  const [schedules, setSchedules] = useState([]);
  const [scheduleIdx, setScheduleIdx] = useState(null);
  const [role, setRole] = useState(1); // 1: 主教练, 0: 副教练
  const [roundsCount, setRoundsCount] = useState({});
  const [currentRound, setCurrentRound] = useState(1);
  const [editing, setEditing] = useState(false);
  const [editData, setEditData] = useState([]);
  const [playersDrawerOpen, setPlayersDrawerOpen] = useState(false);
  const [playersForSchedule, setPlayersForSchedule] = useState([]);

  /* P1 评奖方式 */
  const [awardConfig, setAwardConfig] = useState({ ...DEFAULT_AWARD_CONFIG, prizes: [...DEFAULT_PRIZES] });

  /* P2 比赛轮次 */
  const [rounds, setRounds] = useState([]);
  const [activeRoundId, setActiveRoundId] = useState(null);
  const [roundModalOpen, setRoundModalOpen] = useState(false);
  const [roundModalInitData, setRoundModalInitData] = useState(null);
  const [roundSlotsMap, setRoundSlotsMap] = useState({});

  /* 赛事状态 */
  const [eventStatus, setEventStatus] = useState(null);
  const [eventItemsData, setEventItemsData] = useState([]);

  useEffect(() => {
    if (event?.id) {
      request.get(`/events/info/${event.id}`).then((data) => {
        setEventStatus(data?.status ?? null);
        setEventItemsData(data?.items || []);
      });
    }
  }, [event?.id]);

  const unit = useMemo(() => {
    return activeItem && activeItem.minPlayer !== null ? '队伍' : '人';
  }, [activeItem]);

  const applyCount = useMemo(() => {
    if (!activeItem?.id || !eventItemsData.length) return null;
    const found = eventItemsData.find((it) => it.id === activeItem.id);
    return found?.apply ?? null;
  }, [eventItemsData, activeItem?.id]);

  useEffect(() => {
    if (activeItem?.id) {
      request
        .get(`/events/schedule/round/player/${activeItem?.id}`)
        .then(setRoundsCount);
    }
  }, [activeItem?.id]);

  /**
   * loadData：加载赛项的历史赛程列表（用于旧版展示），
   * 同时将后端返回数据回显到 rounds / roundSlotsMap / awardConfig
   */
  const selectionMethodReverseMap = { 1: '排位法', 2: '单败淘汰', 3: '双败淘汰' };
  const loadData = useCallback((id) => {
    request.get(`/events/admin/schedule/${id}`).then((data) => {
      const list = data || [];
      setSchedules(list);

      /* 若接口无数据则不回显 */
      if (list.length === 0) return;

      /* 按 round 编号分组 */
      const roundMap = {};
      for (const item of list) {
        const rNum = item.round;
        if (!roundMap[rNum]) roundMap[rNum] = [];
        roundMap[rNum].push(item);
      }

      const sortedRoundNums = Object.keys(roundMap)
        .map(Number)
        .sort((a, b) => a - b);

      /* 构建 rounds 数组 */
      const newRounds = sortedRoundNums.map((rNum) => {
        const first = roundMap[rNum][0];
        return {
          id: rNum,
          index: rNum,
          eliminationType: selectionMethodReverseMap[first.selectionMethod] || '排位法',
          matchType: MATCH_TYPE_OPTIONS[first.matchType - 1] || '排位赛',
          advanceCount: first.promotionCount ?? 0,
          boCount: first.boCount ?? 1,
          roundCount: 1,
          /* 回显轮空处理方式（0=直接晋级，1=与败方对战；后端未返回时保持 null，详情显示 —） */
          allowByeMatch: first.allowByeMatch ?? null,
        };
      });

      /* 构建 roundSlotsMap */
      const newSlotsMap = {};
      for (const rNum of sortedRoundNums) {
        /* 同一轮次按 matchPlace 聚合为 slot（接口一条记录即一个场地） */
        const slotMap = {};
        for (const item of roundMap[rNum]) {
          const key = item.matchPlace + '_' + item.matchStartTime;
          if (!slotMap[key]) {
            slotMap[key] = {
              matchPlace: item.matchPlace,
              matchStartTime: item.matchStartTime,
              matchEndTime: item.matchEndTime,
              player: item.player,
              refereeList: item.refereeList || [],
              scheduleId: item.scheduleId,
              statueText: item.statueText,
            };
          }
        }
        newSlotsMap[rNum] = Object.values(slotMap);
      }

      setRounds(newRounds);
      setRoundSlotsMap(newSlotsMap);
      if (newRounds.length > 0) setActiveRoundId(newRounds[0].id);

      /* 回显 awardConfig（使用第一条记录的奖项设置） */
      const first = list[0];
      if (first.awardMode) {
        const modeStr = first.awardMode === 1 ? 'percentage' : 'traditional';
        const roundingStr = first.awardRoundLogic === 2 ? 'floor' : 'ceil';
        let prizes = [...DEFAULT_PRIZES];
        try {
          const parsed = typeof first.awardConfig === 'string' && first.awardConfig
            ? JSON.parse(first.awardConfig)
            : (first.awardConfig || {});
          const keys = Object.keys(parsed);
          if (keys.length > 0) {
            prizes = keys.map((k, i) => ({
              label: `${['一', '二', '三', '四', '五'][i] || k}等奖`,
              value: Number(parsed[k]) || 0,
            }));
          }
        } catch (_) { /* 解析失败则保留默认值 */ }
        setAwardConfig({ mode: modeStr, rounding: roundingStr, prizes });
      }

      /* 重置快照，避免回显后误触发"未保存"提示 */
      const snap = { itemId: id, awardConfig, rounds: newRounds, roundSlotsMap: newSlotsMap };
      lastSavedDataRef.current = JSON.stringify(snap);
    });
  }, []);

  useEffect(() => {
    if (activeItem?.id) loadData(activeItem.id);
  }, [activeItem, loadData]);

  const [coachDrawerOpen, setCoachDrawerOpen] = useState(false);
  const [coaches, setCoaches] = useState([]);

  const showCoachDrawer = (idx, roleType) => {
    setScheduleIdx(idx);
    setRole(roleType);
    setCoachDrawerOpen(true);
  };

  const closeCoachDrawer = () => {
    setCoaches([]);
    setCoachDrawerOpen(false);
  };

  const showPlayersDrawer = (scheduleId) => {
    const schedule = schedules.find((s) => s.scheduleId === scheduleId);
    setPlayersForSchedule(schedule?.playerList || []);
    setPlayersDrawerOpen(true);
  };

  const closePlayersDrawer = () => {
    setPlayersDrawerOpen(false);
    setPlayersForSchedule([]);
  };

  const toggleCoachSelect = (data, multiple) => {
    if (multiple) {
      setCoaches((prev) =>
        prev.some((r) => r.id === data.id)
          ? prev.filter((r) => r.id !== data.id)
          : [...prev, data]
      );
    } else {
      setCoaches((prev) => (prev.some((r) => r.id === data.id) ? [] : [data]));
    }
  };

  /**
   * confirmCoachAssign：确认分配裁判到指定场次
   * - 将选中的裁判赋予当前 role（1=主裁判，0=助理裁判）
   * - 若被选裁判已存在于对立角色中，则执行双向互换：
   *     对立角色中被"抢走"的人，由当前角色的旧裁判顶替其空缺位置
   *   场景举例：
   *     主裁A / 助理B → 选助理为A → 主裁变B，助理为A（双向互换）
   *     主裁A / 助理空 → 选助理为A → 主裁空，助理为A（A 移位，无人可补）
   *
   * 两类裁判对象结构说明：
   *   - invite/list（coaches 来源）：id=序列号，refereeId=真实业务 id
   *   - schedule refereeList 后端回显：id=真实业务 id，refereeId="0"
   *   - 经过本函数写入后的 refereeList：保留 coaches 原始结构（id=序列号，refereeId=真实业务 id）
   * 因此需用统一辅助函数 getBusinessId 取真实业务 id，兼容三种情况。
   */
  const confirmCoachAssign = useCallback(async () => {
    if (coaches.length === 0 || scheduleIdx === null) return;
    /*
     * getBusinessId：统一提取裁判的真实业务 id
     * - coaches / 已写入 refereeList 的对象：refereeId 为有效业务 id（非 "0"），优先使用
     * - 后端回显对象：refereeId 为 "0"，真实 id 在 id 字段中
     */
    const getBusinessId = (r) => (r.refereeId && r.refereeId !== '0') ? r.refereeId : r.id;
    const newIds = new Set(coaches.map((c) => getBusinessId(c)));
    const oppositeRole = role === 1 ? 0 : 1;
    setEditData((prev) => {
      const next = prev.map((s, idx) => {
        if (scheduleIdx !== idx) return s;
        const refereeList = s.refereeList || [];
        /*
         * 找出新选裁判中原本在「对立角色」的人（跨角色裁判）
         * 这些人将从对立角色移走，触发双向互换逻辑
         */
        const crossingIds = new Set(
          refereeList
            .filter((r) => r.role === oppositeRole && newIds.has(getBusinessId(r)))
            .map((r) => getBusinessId(r))
        );
        /*
         * 若存在跨角色裁判，则将当前角色的旧裁判"换到"对立角色：
         * - 收集当前 role 的旧裁判列表
         * - 赋予 oppositeRole 角色后补充到结果中
         * 若无跨角色裁判（普通替换），则 swappedToOpposite 为空数组
         */
        const currentRoleOldReferees = crossingIds.size > 0
          ? refereeList
              .filter((r) => r.role === role)
              .map((r) => ({ ...r, role: oppositeRole }))
          : [];
        /*
         * 构建新的 refereeList：
         * 1. 保留对立角色中未被"抢走"的人
         * 2. 将当前角色旧裁判换到对立角色（仅双向互换时有值）
         * 3. 加入新选的裁判（目标角色）
         */
        const newRefereeList = [
          ...refereeList.filter(
            (r) => r.role === oppositeRole && !crossingIds.has(getBusinessId(r))
          ),
          ...currentRoleOldReferees,
          ...coaches.map((c) => ({ ...c, role })),
        ];
        return { ...s, refereeList: newRefereeList };
      });
      return next;
    });
    setCoaches([]);
    setCoachDrawerOpen(false);
    setScheduleIdx(null);
  }, [role, coaches, scheduleIdx]);

  useEffect(() => {
    if (coaches.length === 1) {
      confirmCoachAssign();
    }
  }, [coaches, role, confirmCoachAssign]);

  // const openEditScheduleDrawer = async () => {
  //   setEditScheduleDrawer(true);
  // };
  const editSchedule = () => {
    setEditing(true);
    setEditData([...(roundSlotsMap[activeRoundId] || [])]);
  };
  const handleItemChange = (idx, field, value) => {
    setEditData((prev) => {
      const next = [...prev];
      next[idx] = {
        ...next[idx],
        [field]: value,
      };
      return next;
    });
  };
  /**
   * saveEdit：校验当前编辑的场次数据（场地名称、人数、时间、主/助理裁判），
   * 验证通过后更新本地状态并立即向后端提交，
   * 与右上角"保存赛程"应用相同的完整校验与接口调用逻辑。
   * 使用 newSlotsMap（手动合并）解决 React 异步状态导致的旧值问题。
   */
  const saveEdit = useCallback(async () => {
    const missMatchPlace = editData.some((s) => !s.matchPlace);
    const missPlayerCount = editData.some((s) => !s.player || s.player <= 0);
    const missTime = editData.some((s) => !s.matchStartTime || !s.matchEndTime);
    const missReferee = editData.some(
      (s) =>
        !s.refereeList?.length || !s.refereeList?.some((r) => r.role === 1)
    );
    const missViceReferee = editData.some(
      (s) =>
        !s.refereeList?.length || !s.refereeList?.some((r) => r.role === 0)
    );
    const totalAssigned = editData.reduce((sum, s) => sum + (Number(s.player) || 0), 0);
    if (missMatchPlace) {
      message.warning('请填写场地名称');
      return;
    }
    if (missPlayerCount) {
      message.warning(`请填写赛程的${unit}数`);
      return;
    }
    if (missTime) {
      message.warning('请填写赛程的比赛时间');
      return;
    }
    if (missReferee) {
      message.warning('请指定主裁判');
      return;
    }
    if (missViceReferee) {
      message.warning('请指定助理裁判');
      return;
    }
    /*
     * 手动合并最新场次数据：setRoundSlotsMap 为异步更新，
     * 若直接调用 handleSaveAll() 会读取旧的 roundSlotsMap 闭包值。
     * 因此先构建合并后的新映射，再同时更新本地状态与提交后端。
     */
    const newSlotsMap = { ...roundSlotsMap, [activeRoundId]: [...editData] };
    setRoundSlotsMap(newSlotsMap);
    setEditing(false);
    /* 同步提交后端，应用与右上角"保存赛程"完全一致的校验与接口调用逻辑 */
    // eslint-disable-next-line react-hooks/exhaustive-deps
    await handleSaveAll(newSlotsMap);
  }, [unit, editData, activeRoundId, roundSlotsMap]);

  /* P2 轮次弹窗确认处理 */
  const handleRoundConfirm = useCallback(
    (formData) => {
      if (roundModalInitData) {
        setRounds((prev) =>
          prev.map((r) => (r.id === roundModalInitData.id ? { ...r, ...formData } : r))
        );
      } else {
        const newRound = { ...formData, id: Date.now(), index: rounds.length + 1 };
        setRounds((prev) => [...prev, newRound]);
        setActiveRoundId(newRound.id);
        setEditing(false);
        setEditData([]);
      }
      setRoundModalOpen(false);
      setRoundModalInitData(null);
    },
    [roundModalInitData, rounds.length]
  );

  /**
   * handleSaveAll：校验赛程完整性后调用 POST /events/admin/schedule 提交保存
   * 将前端 awardConfig / rounds / roundSlotsMap 转换为后端接口参数格式
   * @param {Object} [slotsMapOverride] - 可选场次数据覆盖，用于分配场次保存时传入最新合并数据，
   *   绕过 setRoundSlotsMap 的异步特性，确保提交的是最新值
   */
  const handleSaveAll = useCallback(async (slotsMapOverride) => {
    /* 若调用方传入最新 slotsMap（如分配场次保存），则以其为准；否则读取当前状态 */
    const activeSlotsMap = slotsMapOverride ?? roundSlotsMap;
    /* 第一步：必须包含最终轮（advanceCount === 0） */
    const hasFinalRound = rounds.some((x) => x.advanceCount === 0);
    if (!hasFinalRound) {
      message.warning('请至少添加一个最终轮次（可晋级人数为 0 的轮次），再保存赛程');
      return;
    }
    if (awardConfig.mode === 'percentage') {
      const total = awardConfig.prizes.reduce((s, p) => s + (Number(p.value) || 0), 0);
      if (total > 100) {
        message.warning('百分比奖项总和超出100%，请调整');
        return;
      }
    }
    if (rounds.length === 0) {
      message.warning('请至少添加一个比赛轮次');
      return;
    }
    /* 检查每个轮次是否完整填写海选方法/比赛方式/晋级人数；循环赛无需海选方法 */
    for (const r of rounds) {
      const needElimination = r.matchType !== '循环赛';
      if ((needElimination && !r.eliminationType) || !r.matchType || r.advanceCount === undefined || r.advanceCount === null) {
        message.warning(`第${r.index}轮信息未填写完整，请检查海选方法、比赛方式或晋级人数`);
        return;
      }
    }

    /* 第二步：检查每个轮次的人数分配是否达到满额（收集所有未达标轮次并一次性提示） */
    const insufficient = [];
    for (const r of rounds) {
      const slots = activeSlotsMap[r.id] || [];
      const assigned = slots.reduce((s, it) => s + (Number(it.player) || 0), 0);
      const expected = r.index === 1 ? (applyCount ?? null) : (rounds.find((x) => x.index === r.index - 1)?.advanceCount ?? applyCount);
      if (expected !== null && expected !== undefined) {
        if (assigned !== expected) {
          insufficient.push({ index: r.index, assigned, expected });
        }
      }
    }
    if (insufficient.length > 0) {
      const parts = insufficient.map((it) => `第${it.index}轮（已分配 ${it.assigned}/${it.expected}）`);
      message.warning(`以下轮次人数未达标：${parts.join('，')}，请补充后再保存`);
      return;
    }

    /* 构建接口参数：将前端结构转换为后端所需格式 */
    const selectionMethodMap = { '排位法': 1, '单败淘汰': 2, '双败淘汰': 3 };
    const apiRoundsArr = [];
    for (const r of rounds) {
      const slots = activeSlotsMap[r.id] || [];
      const matchTypeIdx = MATCH_TYPE_OPTIONS.indexOf(r.matchType) + 1;
      for (const slot of slots) {
        apiRoundsArr.push({
          round: r.index,
          matchPlace: slot.matchPlace,
          // matchStartTime: slot.matchStartTime,
          // matchEndTime: slot.matchEndTime,
          matchStartTime: slot.matchStartTime ? dayjs(slot.matchStartTime).toISOString() : '',
          matchEndTime: slot.matchEndTime ? dayjs(slot.matchEndTime).toISOString() : '',
          player: Number(slot.player) || 0,
          selectionMethod: r.matchType === '循环赛' ? -1 : (selectionMethodMap[r.eliminationType] ?? 1),
          matchType: matchTypeIdx > 0 ? matchTypeIdx : 1,
          boCount: r.boCount ?? 1,
          promotionCount: r.advanceCount ?? 0,
          defenseRound: 0,
          /* 轮空队伍处理方式：0=直接晋级，1=与败方对战（后端字段名: allowByeMatch） */
          allowByeMatch: r.allowByeMatch ?? 0,
          refereeList: slot.refereeList || [],
        });
      }
    }

    const awardConfigObj = {};
    (awardConfig.prizes || []).forEach((p, i) => {
      awardConfigObj[String(i + 1)] = Number(p.value) || 0;
    });

    const payload = {
      itemId: activeItem?.id,
      awardMode: awardConfig.mode === 'percentage' ? 1 : 2,
      awardConfig: awardConfigObj,
      awardRoundLogic: awardConfig.rounding === 'ceil' ? 1 : 2,
      rounds: apiRoundsArr,
    };

    try {
      await request.post('/events/admin/schedule', payload);
      /* 标记为已保存的快照（前端，用于切换赛项时的未保存检测），使用 activeSlotsMap 保证快照反映实际提交数据 */
      const snapshotPayload = { itemId: activeItem?.id, awardConfig, rounds, roundSlotsMap: activeSlotsMap };
      lastSavedDataRef.current = JSON.stringify(snapshotPayload);
      message.success('赛程保存成功');
      /* 保存成功后重新拉取后端最新数据，确保页面渲染与后端一致 */
      if (activeItem?.id) loadData(activeItem.id);
    } catch (e) {
      message.error('赛程保存失败，请稍后重试');
    }
  }, [awardConfig, rounds, activeItem, roundSlotsMap, applyCount, loadData]);

  const lastSavedDataRef = useRef(null);

  const getCurrentPayload = () => ({ itemId: activeItem?.id, awardConfig, rounds, roundSlotsMap });

  // 初始化已保存快照（切换赛项或首次加载时）
  useEffect(() => {
    lastSavedDataRef.current = JSON.stringify(getCurrentPayload());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeItem?.id]);

  // const handleSwitchItem = (value) => {
  //   const current = JSON.stringify(getCurrentPayload());
  //   if (lastSavedDataRef.current && current !== lastSavedDataRef.current) {
  //     Modal.confirm({
  //       title: '当前赛项有未保存的更改',
  //       content: '是否先保存当前赛项的赛程？',
  //       okText: '保存并切换',
  //       cancelText: '取消',
  //       onOk: async () => {
  //         handleSaveAll();
  //         // 等待短暂时间确保保存逻辑（模拟）完成
  //         setTimeout(() => {
  //           setActiveItem(value);
  //           setEditing(false);
  //           setEditData([]);
  //           setCurrentRound(1);
  //           setAwardConfig({ ...DEFAULT_AWARD_CONFIG, prizes: [...DEFAULT_PRIZES] });
  //           setRounds([]);
  //           setActiveRoundId(null);
  //         }, 200);
  //       },
  //       onCancel: () => {
  //         // 询问是否放弃并切换
  //         Modal.confirm({
  //           title: '放弃更改并切换？',
  //           content: '放弃未保存的更改将无法恢复。确认放弃并切换赛项吗？',
  //           okText: '放弃并切换',
  //           cancelText: '取消',
  //           onOk: () => {
  //             setActiveItem(value);
  //             setEditing(false);
  //             setEditData([]);
  //             setCurrentRound(1);
  //             setAwardConfig({ ...DEFAULT_AWARD_CONFIG, prizes: [...DEFAULT_PRIZES] });
  //             setRounds([]);
  //             setActiveRoundId(null);
  //           },
  //         });
  //       },
  //     });
  //   } else {
  //     setActiveItem(value);
  //     setEditing(false);
  //     setEditData([]);
  //     setCurrentRound(1);
  //     setAwardConfig({ ...DEFAULT_AWARD_CONFIG, prizes: [...DEFAULT_PRIZES] });
  //     setRounds([]);
  //     setActiveRoundId(null);
  //   }
  // };

  const handleSwitchItem = (value) => {
  // 仅在处于“编辑分配场次”状态时提示用户保存
  if (editing) {
    Modal.confirm({
      title: '当前赛项有未保存数据',
      content: '当前赛项有未保存数据，请点击【保存赛程】按钮保存。',
      okText: '放弃并切换',
      cancelText: '去保存',
      onOk: () => {
        // 放弃并切换：直接切换（不再在此处提交接口）
        setActiveItem(value);
        setEditing(false);
        setEditData([]);
        setCurrentRound(1);
        setAwardConfig({ ...DEFAULT_AWARD_CONFIG, prizes: [...DEFAULT_PRIZES] });
        setRounds([]);
        setActiveRoundId(null);
      },
      onCancel: () => {
        // 去保存：只关闭弹窗，用户可点击页面右上角的“保存赛程”按钮来保存
      },
    });
  } else {
    // 非编辑状态，直接切换
    setActiveItem(value);
    setEditing(false);
    setEditData([]);
    setCurrentRound(1);
    setAwardConfig({ ...DEFAULT_AWARD_CONFIG, prizes: [...DEFAULT_PRIZES] });
    setRounds([]);
    setActiveRoundId(null);
  }
};

  const isReadOnly = eventStatus === 4 || eventStatus === 5 || eventStatus === 6;

  /*
   * 当前选中轮次的待分配人数：
   * - 第1轮 = applyCount（报名总人数）
   * - 第N轮 = 上一轮（index-1）的 advanceCount
   */
  const currentRoundApply = useMemo(() => {
    if (!activeRoundId) return applyCount;
    const r = rounds.find((x) => x.id === activeRoundId);
    if (!r || r.index === 1) return applyCount;
    const prev = rounds.find((x) => x.index === r.index - 1);
    return prev?.advanceCount ?? applyCount;
  }, [activeRoundId, rounds, applyCount]);

  /* 新增轮次时可展轮次数的上限 */
  const newRoundMaxApply = useMemo(() => {
    if (rounds.length === 0) return applyCount;
    return rounds[rounds.length - 1]?.advanceCount ?? applyCount;
  }, [rounds, applyCount]);

  /* 编辑已有轮次时，展轮次数的上限 */
  const editRoundMaxApply = useMemo(() => {
    if (!roundModalInitData) return newRoundMaxApply;
    if (roundModalInitData.index === 1) return applyCount;
    const prev = rounds.find((x) => x.index === roundModalInitData.index - 1);
    return prev?.advanceCount ?? applyCount;
  }, [roundModalInitData, rounds, applyCount, newRoundMaxApply]);

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-2">
        <ItemTabs
          items={items}
          activeType={activeItem}
          onChange={(value) => {
            handleSwitchItem(value);
          }}
        />
        {eventStatus === 3 && (
          <button
            className="px-6 pl-5 py-2 rounded-lg text-sm font-medium transition-colors duration-200 inline-flex items-center gap-2 bg-blue-600 text-white hover:bg-blue-700 cursor-pointer"
            onClick={() => handleSaveAll()}
          >
            <CheckOutlined />
            <span>保存赛程</span>
          </button>
        )}
      </div>

      {(eventStatus === 3 || isReadOnly) ? (
        <>
      {/* P1 选择评奖方式 */}
      <AwardSection awardConfig={awardConfig} onChange={setAwardConfig} readOnly={isReadOnly} applyCount={applyCount} unit={unit} />

      {/* P2 编辑比赛轮次 */}
      <div className="bg-white rounded-xl shadow-sm p-6 space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="text-base font-semibold text-slate-800">比赛轮次</h3>
          {activeRoundId && currentRoundApply !== null && (
            <span className="text-sm text-slate-500">待分配选手：<span className="text-slate-800 font-medium">{currentRoundApply}</span>人（队）</span>
          )}
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {rounds.map((r) => (
            <button
              key={r.id}
              type="button"
              className={`px-4 py-1.5 rounded-lg border text-sm font-medium transition-all ${
                activeRoundId === r.id
                  ? 'bg-blue-600 text-white border-blue-600'
                  : 'bg-white border-slate-200 text-slate-700 hover:bg-blue-50 hover:text-blue-700'
              }`}
              onClick={() => {
                setActiveRoundId(r.id);
                setCurrentRound(r.index);
                setEditing(false);
                setEditData(roundSlotsMap[r.id] || []);
              }}
            >
              第{r.index}轮
            </button>
          ))}
          {!isReadOnly && !rounds.some((r) => r.advanceCount === 0) && (
          <button
            type="button"
            className="px-4 py-1.5 rounded-lg border border-dashed border-blue-400 text-blue-600 text-sm font-medium hover:bg-blue-50 transition-colors"
            onClick={() => {
              setRoundModalInitData(null);
              setRoundModalOpen(true);
            }}
          >
            + 增加轮次
          </button>
          )}
        </div>
        {/* 当前轮次详情 */}
        {activeRoundId &&
          (() => {
            const r = rounds.find((x) => x.id === activeRoundId);
            if (!r) return null;
            return (
              <>
              <div className="border border-slate-100 rounded-xl p-4 bg-slate-50 space-y-3 text-sm text-slate-700">
                <div className="flex items-center justify-between">
                  <span className="font-medium text-slate-900">第{r.index}轮详情</span>
                  <div className="flex gap-2">
                    {!isReadOnly && (
                    <button
                      type="button"
                      className="px-3 py-1 text-xs bg-white border border-slate-200 text-slate-700 rounded hover:bg-blue-50 hover:text-blue-600 transition-colors"
                      onClick={() => {
                        setRoundModalInitData(r);
                        setRoundModalOpen(true);
                      }}
                    >
                      修改
                    </button>
                    )}
                    {!isReadOnly && (
                    <button
                      type="button"
                      className="px-3 py-1 text-xs bg-white border border-red-200 text-red-600 rounded hover:bg-red-50 transition-colors"
                      onClick={() => {
                        Modal.confirm({
                          title: `确认删除第${r.index}轮？`,
                          content: '删除后该轮次及分配的场地数据将一并清除',
                          okText: '确认删除',
                          okButtonProps: { danger: true },
                          cancelText: '取消',
                          onOk: () => {
                            setRounds((prev) => {
                              const next = prev
                                .filter((x) => x.id !== r.id)
                                .map((x, i) => ({ ...x, index: i + 1 }));
                              return next;
                            });
                            setActiveRoundId(() => {
                              const remaining = rounds.filter((x) => x.id !== r.id);
                              return remaining.length > 0 ? remaining[0].id : null;
                            });
                          },
                        });
                      }}
                    >
                      删除
                    </button>
                    )}
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <span className="text-slate-500">海选方法：</span>
                    {/* 循环赛无海选方法，展示横杠 */}
                    {r.matchType === '循环赛' ? '—' : (r.eliminationType || '—')}
                  </div>
                  <div>
                    <span className="text-slate-500">比赛方式：</span>
                    {r.matchType || '—'}
                  </div>
                  <div>
                    <span className="text-slate-500">可晋级人数：</span>
                    {r.advanceCount === 0 ? (
                      <span className="text-blue-600">最终轮次</span>
                    ) : (
                      `${r.advanceCount}人`
                    )}
                  </div>
                  {r.matchType === 'BO赛' && (
                    <div>
                      <span className="text-slate-500">BO次数：</span>BO{r.boCount}
                    </div>
                  )}
                  {/* 轮空队伍：仅 BO赛 时显示，0=直接晋级，1=与败方对战；后端未返回时展示 — */}
                  {r.matchType === 'BO赛' && (
                    <div>
                      <span className="text-slate-500">轮空队伍：</span>
                      {r.allowByeMatch == null ? '—' : r.allowByeMatch === 1 ? '与败方对战' : '直接晋级'}
                    </div>
                  )}
                  {r.matchType === '回合攻防赛' && (
                    <div>
                      <span className="text-slate-500">回合数：</span>
                      {r.roundCount}回合
                    </div>
                  )}
                </div>
              </div>
              {/* 分配选手场地 */}
              <div className="mt-4">
                <div className="flex items-center justify-between mb-3">
                  <span className="text-base font-semibold text-slate-800">分配选手场地</span>
                  {(roundSlotsMap[r.id] || []).length > 0 && !editing && !isReadOnly && (
                    <button
                      className="inline-flex items-center gap-2 px-3 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700 transition-colors cursor-pointer"
                      onClick={editSchedule}
                    >
                      编辑
                    </button>
                  )}
                </div>
                <div className="flex flex-wrap gap-4">
                  {!editing &&
                    (roundSlotsMap[r.id] || []).map((s, idx) => (
                      <SItem
                        s={s}
                        idx={idx}
                        unit={unit}
                        key={s.scheduleId || idx}
                        showPlayersDrawer={showPlayersDrawer}
                        editable={false}
                      />
                    ))}
                  {editing &&
                    editData.map((s, idx) => (
                      <SItem
                        s={s}
                        idx={idx}
                        unit={unit}
                        key={(s.scheduleId || '') + idx}
                        showPlayersDrawer={showPlayersDrawer}
                        showCoachDrawer={showCoachDrawer}
                        editable
                        onChange={handleItemChange}
                        setEditData={setEditData}
                      />
                    ))}
                  {!isReadOnly && (editing || (roundSlotsMap[r.id] || []).length === 0) && (
                    <button
                      type="button"
                      className="w-100 bg-white rounded-xl shadow-sm border border-slate-100 flex flex-col h-75 min-w-[360px] items-center justify-center transition-shadow hover:shadow-md group cursor-pointer"
                      aria-label="增加场次"
                      onClick={() => {
                        const totalAssigned = editData.reduce(
                          (sum, s) => sum + (Number(s.player) || 0), 0
                        );
                        // 若有报名人数限制，则新增场次至少需要 1 人（队），超额时阻止新增并提示
                        if (currentRoundApply !== null) {
                          const remaining = currentRoundApply - totalAssigned;
                          if (remaining <= 0) {
                            message.warning(
                              `当前轮次最多分配 ${currentRoundApply} 人（队），已全部分配完毕`
                            );
                            return;
                          }
                          if (remaining < 1) {
                            message.warning(
                              `剩余可分配人数不足（剩余 ${Math.max(0, remaining)} 人/队），无法新增场次`
                            );
                            return;
                          }
                        }
                        if (!editing) {
                          setEditing(true);
                          setEditData([...(roundSlotsMap[r.id] || []), {}]);
                        } else {
                          setEditData((prev) => [...prev, {}]);
                        }
                      }}
                    >
                      <span className="flex flex-col gap-2 items-center text-blue-500">
                        <PlusOutlined className="text-2xl" />
                        <span className="text-lg">增加场次</span>
                      </span>
                    </button>
                  )}
                </div>
                {!isReadOnly && editing && (
                  <div className="flex justify-center mt-4">
                    <button
                      className="px-6 py-2 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 transition-colors"
                      onClick={saveEdit}
                    >
                      保存
                    </button>
                    <button
                      className="ml-3 px-6 py-2 bg-slate-100 text-slate-700 rounded-lg font-medium hover:bg-slate-200 transition-colors"
                      onClick={() => setEditing(false)}
                    >
                      取消
                    </button>
                  </div>
                )}
              </div>
              </>
            );
          })()}
        {rounds.length === 0 && (
          <div className="text-center py-8 text-slate-400 text-sm">
            暂无轮次，点击「+ 增加轮次」开始配置
          </div>
        )}
      </div>
      {/* 底部保存按钮已移至右上角浮动，底部按钮移除以避免重复 */}
        </>
      ) : (
        <div className="bg-white rounded-xl shadow-sm flex flex-col items-center justify-center py-20">
          <svg width="120" height="90" viewBox="0 0 120 90" fill="none" xmlns="http://www.w3.org/2000/svg">
            <rect x="18" y="12" width="72" height="58" rx="8" fill="#E8EDF5" />
            <rect x="26" y="22" width="40" height="6" rx="3" fill="#C5CDD9" />
            <rect x="26" y="34" width="56" height="5" rx="2.5" fill="#D4DAE4" />
            <rect x="26" y="44" width="48" height="5" rx="2.5" fill="#D4DAE4" />
            <circle cx="84" cy="46" r="16" fill="#DBEAFE" />
            <path d="M76 46l5 5 9-10" stroke="#3B82F6" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
          <p className="mt-6 text-sm font-semibold text-slate-700">请待报名截止后再编排赛程</p>
        </div>
      )}

      <RefereeSelector
        open={coachDrawerOpen}
        onClose={closeCoachDrawer}
        onConfirm={confirmCoachAssign}
        name={role === 1 ? '主教练' : '副教练'}
        selectedItems={coaches}
        onToggle={toggleCoachSelect}
        multiple={false}
        api="/events/admin/referee/invite/list"
        addPath="/referee"
      />
      {/* contestFormat 透传至弹窗，用于动态限制海选方法/比赛方式选项 */}
      <RoundModal
        open={roundModalOpen}
        onClose={() => {
          setRoundModalOpen(false);
          setRoundModalInitData(null);
        }}
        onConfirm={handleRoundConfirm}
        initialData={roundModalInitData}
        maxApply={roundModalInitData ? editRoundMaxApply : newRoundMaxApply}
        contestFormat={activeItem?.contestFormat}
      />
      <Drawer
        title="选手列表"
        size={600}
        onClose={closePlayersDrawer}
        open={playersDrawerOpen}
        // extra={<ExportButton />}
      >
        <PlayerList key={playersDrawerOpen} players={playersForSchedule} />
      </Drawer>
      {/* <EditSchedules
        key={activeItem.id}
        id={activeItem.id}
        name={activeItem.name + activeItem.groupName}
        data={schedules}
        open={editScheduleDrawer}
        onClose={() => setEditScheduleDrawer(false)}
        onConfirm={() => {
          loadData(activeItem?.id);
          setEditScheduleDrawer(false);
        }}
      /> */}
    </div>
  );
}
